// CommonMark gives `---` two meanings, and the chat system prompt only ever means one of them.
// `api/src/ai/agents/simple.ts` mandates that every deliverable be wrapped between a standalone
// `---` line before it and another after it. When the model writes that closing `---` on the line
// immediately after the last paragraph — no blank line between them — the parser reads the pair as
// a setext heading underline instead of a thematic break: the whole closing paragraph renders at h2
// size and the rule disappears entirely.
//
// This undoes that reading after the fact rather than rewriting the markdown before it is parsed.
// The parser has already decided what is a paragraph, so a `---` inside fenced or indented code, in
// a table, or under a list item never reaches this plugin as a heading at all — none of it needs
// handling here. Running at render time is also what fixes already-persisted messages, with no
// migration and no dependency on the model getting its spacing right.
//
// Trade-off we accept: a genuine setext h2 written with `---` now renders as a paragraph followed
// by an `<hr>`. The prompt tells the model to use ATX `##` for headings and reserves `---` for
// separating sections, so that collision is not one we expect to hit. A `===` underline parses as a
// depth-1 heading and one or two dashes can never be a thematic break, so both are left alone.

interface MarkdownNode {
  type: string;
  depth?: number;
  children?: MarkdownNode[];
  position?: { start: { line: number }; end: { line: number } };
}

// The parser has already established that this line is a valid setext underline, so all that is
// left to read off it is which character it used and how long the run is. Matching only the run
// keeps this working inside a blockquote or a list item, where the line carries a container prefix.
const UNDERLINE_RUN = /(-+|=+)[ \t]*\r?$/;

function isDashUnderlined(node: MarkdownNode, lines: string[]): boolean {
  if (node.type !== 'heading' || node.depth !== 2 || !node.position) return false;
  // An ATX `## Heading` occupies one line; a setext heading spans its text plus the underline.
  if (node.position.start.line === node.position.end.line) return false;

  const run = (lines[node.position.end.line - 1] ?? '').match(UNDERLINE_RUN)?.[1] ?? '';
  return run.startsWith('-') && run.length >= 3;
}

function rewrite(parent: MarkdownNode, lines: string[]): void {
  if (!parent.children) return;

  for (let index = 0; index < parent.children.length; index++) {
    const node = parent.children[index];
    if (!node) continue;

    rewrite(node, lines);

    if (!isDashUnderlined(node, lines)) continue;

    parent.children.splice(index, 1, { type: 'paragraph', children: node.children }, { type: 'thematicBreak' });
    index++;
  }
}

/**
 * Remark plugin: render a chat deliverable's closing `---` as the rule it was meant to be, rather
 * than as the underline of a heading nobody wrote. Pass it after `remark-gfm`.
 */
export function remarkDashRule() {
  return (tree: MarkdownNode, file: unknown) => {
    rewrite(tree, String(file).split('\n'));
  };
}
